import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Circle, CheckCircle, Calendar } from 'lucide-react';
import { InitiativesDB } from '../../utils/initiativesData';
import InitiativeEditModal from './InitiativeEditModal';
import { Button } from '../ui';

function getHealthColor(health) {
  switch (health) {
    case 'green': return '#2EB87A';
    case 'yellow': return '#E5A820';
    case 'red': return '#D94F4F';
    default: return '#6B7280';
  }
}

function getHealthIcon(health) {
  const color = getHealthColor(health);
  const size = 12;
  return <Circle size={size} color={color} fill={color} strokeWidth={2} style={{ verticalAlign: 'middle', marginRight: 2 }} />;
}


function formatDate(dateString) {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function InitiativeCard({ initiative, onView, onSubmitUpdate, onAddToQueue, onEdit }) {
  const healthIcon = getHealthIcon(initiative.overallHealth);
  const healthColor = getHealthColor(initiative.overallHealth);
  const latestUpdate = initiative.statusUpdates?.length > 0 ? initiative.statusUpdates[0] : null;
  const statusBadgeBg = initiative.status === 'in-progress' ? '#10B98120' : initiative.status === 'completed' ? '#10B98120' : '#6B728020';
  const statusBadgeColor = initiative.status === 'in-progress' ? '#10B981' : initiative.status === 'completed' ? '#10B981' : '#6B7280';
  const upcomingMilestones = (initiative.milestones || []).slice(0, 2);

  return (
    <div
      className="initiative-card bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative initiative-card-enhanced"
      style={{
        cursor: 'pointer',
        borderLeft: `4px solid ${healthColor}`,
        background: 'white',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}
      onClick={() => onView(initiative.id)}
      role="button"
      tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onView(initiative.id); } }}
    >
      <div className="p-5">
        <div className="initiative-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 15 }}>
          <div>
            <h3 style={{ margin: '0 0 5px 0', fontWeight: 500, fontSize: 18 }}>{initiative.name}</h3>
            <div className="text-text-dark-secondary" style={{ fontSize: 13 }}>
              Due: {formatDate(initiative.dueDate)} | PM: {initiative.programManagerName || 'Unassigned'}
            </div>
          </div>
          <span style={{ background: statusBadgeBg, color: statusBadgeColor, padding: '4px 12px', borderRadius: 12, fontSize: 12, fontWeight: 600 }}>
            {healthIcon} {initiative.status}
          </span>
        </div>
        <div className="progress-section" style={{ marginBottom: 15 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
            <span className="text-text-dark-secondary" style={{ fontSize: 12 }}>Progress</span>
            <span style={{ fontSize: 12, fontWeight: 600 }}>{initiative.percentComplete ?? 0}% complete</span>
          </div>
          <div style={{ height: 8, background: '#F3F4F6', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ width: `${initiative.percentComplete ?? 0}%`, height: '100%', background: healthColor, transition: 'width 0.3s' }} />
          </div>
        </div>
        {initiative.tags?.length > 0 && (
          <div className="tag-list" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 15 }}>
            {initiative.tags.map(tag => (
              <span key={tag.id} className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-aqua-glow text-aqua" style={{ padding: '4px 12px', borderRadius: 12, fontSize: 12, fontWeight: 500, background: `${tag.color || '#6B7280'}20`, color: tag.color || '#6B7280' }}>
                [{tag.label}]
              </span>
            ))}
          </div>
        )}
        {initiative.kpis?.length > 0 && (
          <div className="bg-surface-warm" style={{ padding: 12, borderRadius: 6, marginBottom: 15 }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: '#374151' }}>Key Metrics</div>
            {initiative.kpis.map(kpi => (
              <div key={kpi.id} style={{ fontSize: 13, marginBottom: 5, color: '#374151' }}>
                {getHealthIcon(kpi.currentHealth)} <strong>{kpi.kpiName}:</strong> {kpi.currentValue} {kpi.unit}
                <span className="text-text-dark-secondary"> (Target: {kpi.targetOperator} {kpi.targetValue} {kpi.unit})</span>
              </div>
            ))}
          </div>
        )}
        {latestUpdate && (
          <div style={{ background: '#EFF6FF', borderLeft: '3px solid #2C5FA8', padding: 10, marginBottom: 15, borderRadius: 4 }}>
            <div className="text-text-dark-secondary" style={{ fontSize: 11, marginBottom: 4 }}>Latest Update ({formatDate(latestUpdate.updateDate)})</div>
            <div style={{ fontSize: 13, color: '#374151' }}>{latestUpdate.summary}</div>
          </div>
        )}
        {upcomingMilestones.length > 0 && (
          <div style={{ marginBottom: 15 }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: '#374151' }}>Upcoming Milestones</div>
            {upcomingMilestones.map(m => (
              <div key={m.id} className="text-text-dark-secondary" style={{ fontSize: 12, marginBottom: 3 }}>
                {m.completed ? <CheckCircle size={14} color="#2EB87A" strokeWidth={2} style={{ verticalAlign: 'middle', marginRight: 4 }} /> : <Calendar size={14} color="#6B7280" strokeWidth={2} style={{ verticalAlign: 'middle', marginRight: 4 }} />} {m.name} <span style={{ color: '#9CA3AF' }}>({formatDate(m.dueDate)})</span>
              </div>
            ))}
          </div>
        )}
        {initiative.team && initiative.team.length > 0 && (
          <div style={{ marginBottom: 15 }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: '#374151' }}>Team Members</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {initiative.team.slice(0, 4).map(member => (
                <div key={member.id} className="text-text-dark-secondary bg-surface-warm" style={{ fontSize: 12, padding: '4px 8px', borderRadius: 4 }}>
                  {member.name}{member.role ? ` (${member.role})` : ''}
                </div>
              ))}
              {initiative.team.length > 4 && (
                <div style={{ fontSize: 12, color: '#9CA3AF', padding: '4px 8px' }}>
                  +{initiative.team.length - 4} more
                </div>
              )}
            </div>
          </div>
        )}
        <div className="initiative-actions flex flex-wrap gap-2 mt-4 pt-4 border-t border-nn-border" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
          <Button type="button" variant="default" size="sm" onClick={() => onView(initiative.id)}>View Details</Button>
          <Button type="button" variant="default" size="sm" onClick={() => onEdit(initiative.id)}>Edit</Button>
          <Button type="button" variant="primary" size="sm" onClick={() => onSubmitUpdate(initiative.id)}>Submit Update</Button>
          <Button type="button" variant="default" size="sm" onClick={() => onAddToQueue(initiative)}>Add to Queue</Button>
        </div>
      </div>
    </div>
  );
}

export default function StrategicInitiativesList() {
  const navigate = useNavigate();
  const [initiatives, setInitiatives] = useState([]);
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [editModalId, setEditModalId] = useState(null);

  const loadInitiatives = useCallback(() => {
    let list = InitiativesDB.getAllInitiatives();
    if (category) list = list.filter(i => i.category === category);
    if (status) list = list.filter(i => i.status === status);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(i =>
        (i.name && i.name.toLowerCase().includes(q)) ||
        (i.description && i.description.toLowerCase().includes(q))
      );
    }
    const enriched = list.map(i => InitiativesDB.getInitiative(i.id));
    setInitiatives(enriched);
  }, [category, status, search]);

  useEffect(() => {
    loadInitiatives();
  }, [loadInitiatives]);

  const handleView = (id) => setEditModalId(id);
  const handleNew = () => navigate('/strategic-initiatives/new');
  const handleSubmitUpdate = (id) => navigate(`/strategic-initiatives/${id}/status-update`);
  const handleAddToQueue = (initiative) => {
    InitiativesDB.addInitiativeToQueue(initiative);
    // Optional: show brief feedback
    if (typeof window !== 'undefined' && window.__NEXO_ADD_TO_QUEUE_FEEDBACK !== false) {
      const msg = document.createElement('div');
      msg.textContent = `"${initiative.name}" added to Priority Queue`;
      msg.style.cssText = 'position:fixed;bottom:20px;right:20px;background:#10B981;color:white;padding:12px 20px;border-radius:8px;font-size:14px;z-index:9999;box-shadow:0 4px 12px rgba(0,0,0,0.15);';
      document.body.appendChild(msg);
      setTimeout(() => msg.remove(), 2500);
    }
  };

  const allInitiatives = InitiativesDB.getAllInitiatives();
  const summaryActive = allInitiatives.filter(i => i.status !== 'completed').length;
  const summaryOnTrack = allInitiatives.filter(i => i.overallHealth === 'green' || i.overallHealth === 'yellow').length;
  const summaryNeedsAttention = allInitiatives.filter(i => i.overallHealth === 'red').length;

  return (
    <div className="dashboard-container">
      <div className="dashboard-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1 className="dashboard-title">Strategic Initiatives</h1>
          <p className="dashboard-subtitle">Practice-level initiatives and goals</p>
        </div>
        <Button type="button" variant="primary" size="sm" onClick={handleNew}>+ New Initiative</Button>
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-5" style={{ marginBottom: 24 }}>
        <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative border-l-4 border-l-aqua">
          <div className="p-5">
            <div className="text-[13px] text-text-dark-secondary font-normal mb-2 block">Active Initiatives</div>
            <div className="text-[34px] font-bold text-text-dark tracking-tight mb-1.5 block">{summaryActive}</div>
            <div className="flex items-center gap-1 text-[13px] mb-4 text-text-dark-secondary">{allInitiatives.length} total initiatives</div>
          </div>
        </div>
        <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative border-l-4 border-l-green-nn">
          <div className="p-5">
            <div className="text-[13px] text-text-dark-secondary font-normal mb-2 block">On Track</div>
            <div className="text-[34px] font-bold text-text-dark tracking-tight mb-1.5 block">{summaryOnTrack}</div>
            <div className="flex items-center gap-1 text-[13px] mb-4 text-green-nn">{allInitiatives.length > 0 ? `${Math.round((summaryOnTrack / allInitiatives.length) * 100)}% on track` : '0% on track'}</div>
          </div>
        </div>
        <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative border-l-4 border-l-red-critical">
          <div className="p-5">
            <div className="text-[13px] text-text-dark-secondary font-normal mb-2 block">Needs Attention</div>
            <div className="text-[34px] font-bold text-text-dark tracking-tight mb-1.5 block">{summaryNeedsAttention}</div>
            <div className="flex items-center gap-1 text-[13px] mb-4 text-red-critical">{summaryNeedsAttention ? 'Behind schedule' : 'All on track'}</div>
          </div>
        </div>
        <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative border-l-4 border-l-yellow-nn">
          <div className="p-5">
            <div className="text-[13px] text-text-dark-secondary font-normal mb-2 block">Total Initiatives</div>
            <div className="text-[34px] font-bold text-text-dark tracking-tight mb-1.5 block">{allInitiatives.length}</div>
            <div className="flex items-center gap-1 text-[13px] mb-4 text-text-dark-secondary">All statuses</div>
          </div>
        </div>
      </div>

      <div className="filter-panel bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative" style={{ marginBottom: 24, padding: 20 }}>
        <div className="filter-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
          <div>
            <label className="block text-[13px] font-medium text-text-dark-secondary mb-1.5">Category</label>
            <select value={category} onChange={e => setCategory(e.target.value)} className="w-full h-9 rounded-lg border border-nn-border bg-nn-white px-3 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30">
              <option value="">All Categories</option>
              <option value="access">Access</option>
              <option value="quality">Quality</option>
              <option value="staffing">Staffing</option>
              <option value="finance">Finance</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-[13px] font-medium text-text-dark-secondary mb-1.5">Status</label>
            <select value={status} onChange={e => setStatus(e.target.value)} className="w-full h-9 rounded-lg border border-nn-border bg-nn-white px-3 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30">
              <option value="">All Status</option>
              <option value="planning">Planning</option>
              <option value="in-progress">In Progress</option>
              <option value="on-hold">On Hold</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          <div>
            <label className="block text-[13px] font-medium text-text-dark-secondary mb-1.5">Search</label>
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search initiatives..." className="w-full h-9 rounded-lg border border-nn-border bg-nn-white px-3 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30" />
          </div>
        </div>
      </div>

      {initiatives.length === 0 ? (
        <div className="text-center py-16 px-5 text-text-dark-secondary bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative" style={{ border: '1px solid #E5E7EB' }}>
          <p className="text-text-dark-secondary" style={{ marginBottom: 20 }}>No initiatives found. Create your first initiative or use Seed Demo Data to add sample initiatives.</p>
          <Button type="button" variant="primary" size="sm" onClick={handleNew}>+ Create Initiative</Button>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-4" style={{ marginBottom: 16 }}>
            <h3 className="text-[15px] font-semibold text-text-dark">Current Initiatives</h3>
          </div>
          <div className="initiatives-list" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {initiatives.map(init => (
              <InitiativeCard
                key={init.id}
                initiative={init}
                onView={handleView}
                onSubmitUpdate={handleSubmitUpdate}
                onAddToQueue={handleAddToQueue}
                onEdit={setEditModalId}
              />
            ))}
          </div>
        </>
      )}

      {editModalId && (
        <InitiativeEditModal
          initiativeId={editModalId}
          onClose={() => setEditModalId(null)}
          onSaved={() => loadInitiatives()}
        />
      )}
    </div>
  );
}
